import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { extractApiError } from "@/lib/api-errors"
import {
  useGetCalUsersQuery,
  useGetCmsUsersQuery,
  useGetTeamMembersQuery,
  useSetTeamMembershipMutation,
} from "../bookingCalendarApi"
import type { CalTeam } from "../bookingCalendarApi"
import { afterMembershipSave, membershipDiff, membershipRoster } from "../lib/teamMembership"
import type { MembershipRow } from "../lib/teamMembership"

/**
 * Who from CMS is on this team, as a checklist.
 *
 * Submitting sends only what moved — see membershipDiff — through
 * api/ams/team-assignments, which addresses people by principal name and creates a Cal account for
 * anyone who does not have one yet. Nothing here writes to Cal by user id, so a member of staff need
 * not exist in Cal before being ticked.
 *
 * Someone added starts with no hours (AB#749), so after a save that added exactly one person the
 * admin is taken to that person's hours in the availability editor; several added get a "Set hours"
 * link each instead.
 */
export function TeamMembershipDialog({ team, open, onOpenChange }: {
  team: CalTeam
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  // Nothing is fetched until the dialog is opened. The roster and the Cal users are already in the
  // cache by then — the calendar behind it runs on them — so only /api/users is a new request.
  const cmsUsersQuery = useGetCmsUsersQuery(undefined, { skip: !open })
  const membersQuery = useGetTeamMembersQuery({ teamId: team.id }, { skip: !open })
  const calUsersQuery = useGetCalUsersQuery(undefined, { skip: !open })

  const [save, saveState] = useSetTeamMembershipMutation()
  const navigate = useNavigate()

  const [search, setSearch] = useState("")
  /** The user's edits, or null while they have made none — in which case the server's answer stands. */
  const [checked, setChecked] = useState<ReadonlySet<string> | null>(null)
  const [failures, setFailures] = useState<{ upn: string; error: string }[]>([])
  const [setHoursLinks, setSetHoursLinks] = useState<{ upn: string; path: string }[]>([])
  const [error, setError] = useState<string | null>(null)

  // A dialog reopened, or pointed at another team, starts from what that team actually looks like
  // now rather than from the edit someone abandoned last time.
  useEffect(() => {
    setSearch("")
    setChecked(null)
    setFailures([])
    setSetHoursLinks([])
    setError(null)
  }, [open, team.id])

  const { rows, unmatched } = useMemo(
    () => membershipRoster(
      cmsUsersQuery.data ?? [],
      membersQuery.data ?? [],
      calUsersQuery.data ?? [],
    ),
    [cmsUsersQuery.data, membersQuery.data, calUsersQuery.data])

  // Someone deactivated may not be put on a team — the server refuses the whole request rather than
  // part of it — so they are not offered. One who is somehow still a member stays on the list: the
  // only thing left to do about them is take them off, and hiding them would hide that.
  const offered = useMemo(() => rows.filter(row => row.user.isActive || row.isMember), [rows])

  const initial = useMemo(
    () => new Set(rows.filter(row => row.isMember).map(row => row.user.userPrincipalName)),
    [rows])

  const selection = checked ?? initial

  // Against every row, not the visible ones: a change made before typing in the search box is still
  // a change the user made, and filtering it out of the diff would quietly drop it.
  const { add, remove } = useMemo(() => membershipDiff(rows, selection), [rows, selection])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return offered

    return offered.filter(row =>
      row.user.displayName.toLowerCase().includes(needle)
      || row.user.userPrincipalName.toLowerCase().includes(needle))
  }, [offered, search])

  const loading = cmsUsersQuery.isLoading || membersQuery.isLoading || calUsersQuery.isLoading
  const loadFailed = !!cmsUsersQuery.error || !!membersQuery.error || !!calUsersQuery.error
  const changeCount = add.length + remove.length

  function toggle(upn: string) {
    const next = new Set(selection)

    if (next.has(upn)) {
      next.delete(upn)
    } else {
      next.add(upn)
    }

    setChecked(next)
  }

  async function submit() {
    if (!team.slug || changeCount === 0) return

    setError(null)
    setFailures([])
    setSetHoursLinks([])

    const result = await save({ teamSlug: team.slug, add, remove })

    if ("error" in result) {
      setError(describe(result.error))
      return
    }

    const next = afterMembershipSave(team.slug, result.data)

    if (next.kind === "editHours") {
      onOpenChange(false)
      navigate(next.path)
      return
    }

    if (next.kind === "setHours") {
      // Some of it landed, or several people did. The list behind this has already been invalidated,
      // so dropping the local edit re-syncs the checkboxes to what actually happened rather than to
      // what was asked for.
      setFailures(result.data.failures)
      setSetHoursLinks(next.links)
      setChecked(null)
      return
    }

    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Team members</DialogTitle>
          <DialogDescription>
            Who takes appointments at {team.name}. Ticking someone puts them on the team.
          </DialogDescription>
        </DialogHeader>

        {!team.slug && (
          <p role="alert" className="text-xs text-amber-700">
            This team isn't set up for appointments yet, so its membership cannot be changed.
          </p>
        )}

        <Input
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search staff"
          aria-label="Search staff"
          disabled={loading || loadFailed}
        />

        <div className="max-h-[45vh] overflow-y-auto -mx-1 px-1">
          {loading && (
            <div className="flex flex-col gap-2 py-1">
              {[...Array(6)].map((_, index) => <Skeleton key={index} className="h-9 rounded-md" />)}
            </div>
          )}

          {loadFailed && (
            <p role="alert" className="py-2 text-sm text-red-700">
              The staff list could not be loaded.
            </p>
          )}

          {!loading && !loadFailed && visible.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">
              {search.trim() ? "No staff match that search." : "There are no staff to show."}
            </p>
          )}

          {!loading && !loadFailed && visible.map(row => (
            <StaffRow
              key={row.user.id}
              row={row}
              checked={selection.has(row.user.userPrincipalName)}
              disabled={!team.slug || saveState.isLoading}
              onToggle={() => toggle(row.user.userPrincipalName)}
            />
          ))}
        </div>

        {/* The team's own roster is the truth, and part of it is out of this dialog's reach: it can
            only name people CMS knows. Saying so beats a list that silently is not the whole team. */}
        {unmatched.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {unmatched.length} member{unmatched.length === 1 ? " is" : "s are"} not a CMS
            user and cannot be managed here: {unmatched.map(member => member.label).join(", ")}.
          </p>
        )}

        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

        <SetHoursLinks links={setHoursLinks} onFollow={() => onOpenChange(false)} />

        {failures.length > 0 && (
          <div role="alert" className="text-sm text-red-700">
            <p>{failures.length} of the changes could not be made:</p>
            <ul className="mt-1 list-disc pl-5 text-xs">
              {failures.map(failure => (
                <li key={failure.upn}>{failure.upn}: {failure.error}</li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="sm:items-center">
          <p className="flex-1 text-xs text-muted-foreground sm:text-left">
            {changeCount === 0
              ? "No changes yet."
              : `${add.length} to add, ${remove.length} to remove.`}
          </p>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saveState.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!team.slug || changeCount === 0 || saveState.isLoading}
          >
            {saveState.isLoading ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The people a save just put on the team, each with a link to set their hours. They start with none
 * (AB#749), so until somebody follows these they are not bookable.
 */
export function SetHoursLinks({ links, onFollow }: {
  links: readonly { upn: string; path: string }[]
  onFollow?: () => void
}) {
  if (links.length === 0) return null

  return (
    <div role="status" className="text-sm">
      <p>Added. They have no hours yet, so nobody can book them until you set some:</p>
      <ul className="mt-1 flex flex-col gap-1 text-xs">
        {links.map(link => (
          <li key={link.upn} className="flex items-center justify-between gap-3">
            <span className="truncate">{link.upn}</span>
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to={link.path} onClick={onFollow}>Set hours</Link>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StaffRow({ row, checked, disabled, onToggle }: {
  row: MembershipRow
  checked: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const id = `team-member-${row.user.id}`

  return (
    <label
      htmlFor={id}
      className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer"
    >
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onToggle} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{row.user.displayName}</div>
        <div className="text-[0.68rem] text-muted-foreground truncate">{row.user.userPrincipalName}</div>
      </div>
      {/* On the team but yet to accept: they are a member and host nothing, which is worth seeing
          from here rather than only from an empty column on the calendar. */}
      {row.isMember && !row.accepted && (
        <Badge variant="secondary" className="h-5 px-1.5 text-[0.68rem] rounded-full">Invited</Badge>
      )}
      {!row.user.isActive && (
        <Badge variant="outline" className="h-5 px-1.5 text-[0.68rem] rounded-full">Deactivated</Badge>
      )}
    </label>
  )
}

/** Turns a failed request into something worth reading. */
function describe(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)

  if (detail) return detail
  if (failure?.status === 403) return "You do not have permission to change team membership."

  return "The team could not be updated."
}
